import React, { useState } from 'react';
import { Box, Divider, Menu, MenuItem, styled, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button } from 'components/Button/Button';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import { MobileAssessmentsPageNavigation } from './MobileAssessmentsPagenavigation';
import { AssessmentsHeaderProps } from './types';
import { AuthSaveFlare } from './AutoSaveFlare';
import { AssessmentsPageInfo } from './AssessmentsPageInfo';
import { AssessmentsHeaderContainer } from './AssessmentsHeaderContainer';

const MobileAssessmentsHeaderContainer = styled(AssessmentsHeaderContainer)(({ theme }) => ({
  padding: theme.spacing(1, 2, 1, 0),
  gap: 'unset',
}));

const PageInfoContainer = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderRadius: theme.spacing(3),
}));

const SubmitButton = styled(Button)(() => ({
  width: 100,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
}));

const DropdownButton = styled(Button)(() => ({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  minWidth: 'unset',
}));

const ButtonDividerWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSaving',
})<{ isSaving: boolean }>(({ theme, isSaving }) => ({
  width: 1,
  display: 'flex',
  backgroundColor: theme.palette.secondary.main,
  opacity: isSaving ? 0.5 : 1,
}));

const ButtonDivider = styled(Divider)(({ theme }) => ({
  height: '70%',
  alignSelf: 'center',
  width: 1,
  backgroundColor: theme.palette.primary.main,
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  paddingBlock: 0,
  minHeight: theme.spacing(5),
}));

export const AssessmentsMobileHeader = ({
  currentPage,
  pages,
  onPageClick,
  onSaveAndClose,
  onSubmit,
  autoSaveStatus,
  isSaving = false,
  isSubmitting = false,
  hasAnsweredQuestion = false,
  isOnline = true,
}: AssessmentsHeaderProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { theme } = useThemeUtils();
  const isMenuOpen = Boolean(menuAnchor);

  const saving = isSaving || isSubmitting;
  const actionsDisabled = saving || !hasAnsweredQuestion || !isOnline;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSaveAndClose = () => {
    handleMenuClose();
    onSaveAndClose();
  };

  const handleSubmit = () => {
    handleMenuClose();
    onSubmit();
  };

  return (
    <MobileAssessmentsHeaderContainer>
      <MobileAssessmentsPageNavigation
        currentPage={currentPage}
        pages={pages}
        onPageClick={onPageClick}
      />

      <PageInfoContainer>
        <AssessmentsPageInfo currentPage={currentPage} totalPages={pages.length} />
        <AuthSaveFlare autoSaveStatus={autoSaveStatus} />
      </PageInfoContainer>

      <ButtonsContainer>
        <SubmitButton
          id="assessment__mobile-header-submit-button"
          variant="primary"
          size="xlarge"
          onClick={handleSubmit}
          disabled={actionsDisabled}
          isLoading={isSaving}
          circularProgressProps={{
            size: 24,
          }}
          aria-label="Submit"
        >
          Submit
        </SubmitButton>
        <ButtonDividerWrapper isSaving={actionsDisabled} aria-hidden="true">
          <ButtonDivider />
        </ButtonDividerWrapper>
        <DropdownButton
          id="assessment__mobile-header-submit-options-button"
          variant="primary"
          size="xlarge"
          onClick={handleMenuClick}
          aria-label="Open submit options menu"
          disabled={actionsDisabled}
        >
          <KeyboardArrowDown fontSize="small" />
        </DropdownButton>
      </ButtonsContainer>

      <Menu
        id="assessment__mobile-header-menu"
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              minWidth: 150,
              borderRadius: 4,
              boxShadow: theme.shadows[4],
              marginTop: theme.spacing(-1),
            },
          },
        }}
      >
        <StyledMenuItem id="assessment__mobile-header-submit-menu-item" onClick={handleSubmit}>
          <Typography variant="body1" color="text.primary" fontWeight={400}>
            Submit
          </Typography>
        </StyledMenuItem>
        <StyledMenuItem
          id="assessment__mobile-header-save-close-menu-item"
          onClick={handleSaveAndClose}
        >
          <Typography variant="body1" color="text.primary" fontWeight={400}>
            Save and Close
          </Typography>
        </StyledMenuItem>
      </Menu>
    </MobileAssessmentsHeaderContainer>
  );
};
